// Enum values + design registry for site popups. Shared by the admin UI
// (client), the public renderer (client), and the server actions — so nothing
// here may import server-only modules (no sql, no storage).

export const POPUP_KINDS = ['image', 'card', 'modal'] as const
export type PopupKind = (typeof POPUP_KINDS)[number]
export const isPopupKind = (x: string): x is PopupKind => (POPUP_KINDS as readonly string[]).includes(x)

// Trigger conditions a popup can arm — any subset, first to fire shows the
// popup. Parameters (delay_seconds, scroll_depth_percent, inactivity_seconds)
// apply only when their trigger is selected.
export const POPUP_TRIGGERS = ['time-on-page', 'exit-intent', 'scroll-depth', 'inactivity'] as const
export type PopupTrigger = (typeof POPUP_TRIGGERS)[number]
export const isPopupTrigger = (x: string): x is PopupTrigger => (POPUP_TRIGGERS as readonly string[]).includes(x)

// Page-type targeting taxonomy. Deliberately popup-specific rather than the
// internal PageType union: product pages split by product_class (the
// distinction admins actually target — prescription vs OTC vs hearable), and
// story pages read as 'article'. How the live page resolves to these is
// match-path.ts (resolvePopupPageTypes); 'compare' is the x-vs-y pages,
// 'directory' the /hearing-aids/compare listing.
//
// A page can resolve to more than one entry: a compare page is always
// 'compare' AND one 'compare-*' per product class on it, so an OTC-vs-
// prescription pair matches both class variants (targeting a class means
// "this pair includes one", not "both are one").
export const POPUP_PAGE_TYPES = [
  'product-otc',
  'product-prescription',
  'product-hearable',
  'article',
  'home',
  'directory',
  'compare',
  'compare-otc',
  'compare-prescription',
] as const
export type PopupPageType = (typeof POPUP_PAGE_TYPES)[number]
export const isPopupPageType = (x: string): x is PopupPageType => (POPUP_PAGE_TYPES as readonly string[]).includes(x)

// 'show': the popup shows on the selected types, ORed with include_paths (a
// page qualifies by path OR by type). 'hide': the selected types are vetoed,
// like exclude_paths. An empty page_types list = no page-type rule at all.
export const POPUP_PAGE_TYPE_MODES = ['show', 'hide'] as const
export type PopupPageTypeMode = (typeof POPUP_PAGE_TYPE_MODES)[number]
export const isPopupPageTypeMode = (x: string): x is PopupPageTypeMode =>
  (POPUP_PAGE_TYPE_MODES as readonly string[]).includes(x)

// Admin picker copy (admin-only UI — exempt from t()/d()).
export const POPUP_PAGE_TYPE_LABELS: Record<PopupPageType, string> = {
  'product-otc': 'OTC hearing aids (product pages)',
  'product-prescription': 'Prescription hearing aids (product pages)',
  'product-hearable': 'Hearables (product pages)',
  article: 'Articles & guides (story pages)',
  home: 'Home page',
  directory: 'Hearing aid directory (/hearing-aids/compare)',
  compare: 'Compare pages (…/x-vs-y)',
  'compare-otc': 'Compare pages including an OTC device',
  'compare-prescription': 'Compare pages including a prescription device',
}

export const POPUP_DEVICES = ['all', 'desktop', 'mobile'] as const
export type PopupDevice = (typeof POPUP_DEVICES)[number]
export const isPopupDevice = (x: string): x is PopupDevice => (POPUP_DEVICES as readonly string[]).includes(x)

// Which HearingTracker property may serve a popup. Existing rows default to
// main only; forum delivery is an explicit editor choice so a broad main-site
// campaign cannot leak into the community by accident.
export const POPUP_SURFACES = ['main', 'forum'] as const
export type PopupSurface = (typeof POPUP_SURFACES)[number]
export const isPopupSurface = (x: string): x is PopupSurface => (POPUP_SURFACES as readonly string[]).includes(x)

// Admin-only copy — exempt from t()/d().
export const POPUP_SURFACE_LABELS: Record<PopupSurface, string> = {
  main: 'Main site (www.hearingtracker.com)',
  forum: 'Forum (forum.hearingtracker.com)',
}

// Forum-only authentication targeting. `all` is deliberately the default so
// existing forum campaigns keep their audience when this field is introduced.
export const POPUP_FORUM_AUDIENCES = ['all', 'visitors', 'users'] as const
export type PopupForumAudience = (typeof POPUP_FORUM_AUDIENCES)[number]
export const isPopupForumAudience = (x: string): x is PopupForumAudience =>
  (POPUP_FORUM_AUDIENCES as readonly string[]).includes(x)

// Admin-only copy — exempt from t()/d().
export const POPUP_FORUM_AUDIENCE_LABELS: Record<PopupForumAudience, string> = {
  all: 'Everyone',
  visitors: 'Visitors (signed out)',
  users: 'Users (signed in)',
}

// Desktop placement; mobile always renders bottom-center.
export const POPUP_POSITIONS = ['bottom-left', 'bottom-right'] as const
export type PopupPosition = (typeof POPUP_POSITIONS)[number]
export const isPopupPosition = (x: string): x is PopupPosition => (POPUP_POSITIONS as readonly string[]).includes(x)

// Which side a card teaser's image sits on (the copy takes the other).
// Deliberately independent of POPUP_POSITIONS: on-screen placement and the
// card's internal reading order are separate choices. Mobile always stacks the
// image above the copy regardless.
export const POPUP_IMAGE_SIDES = ['left', 'right'] as const
export type PopupImageSide = (typeof POPUP_IMAGE_SIDES)[number]
export const isPopupImageSide = (x: string): x is PopupImageSide => (POPUP_IMAGE_SIDES as readonly string[]).includes(x)

// Card teaser desktop column split. The percentage applies after subtracting
// the fixed inter-column gutter; mobile always uses the stacked layout.
export const POPUP_IMAGE_COLUMN_PERCENT_MIN = 25
export const POPUP_IMAGE_COLUMN_PERCENT_MAX = 60
export const POPUP_IMAGE_COLUMN_PERCENT_DEFAULT = 38

// The design registry: what the admin's single "Design" select offers. `kind`
// decides the frame + which content fields apply (image/card = bottom-corner
// teaser, modal = centered dialog); `template` picks the component that draws
// it (src/components/client/site-popups/templates.tsx). A custom one-off
// design is a new component + an entry here — Tailwind stays in code, never
// in the DB.
export interface PopupDesign {
  kind: PopupKind
  template: string
  label: string
  description: string
  /** Whether this design reads popups.url for a visitor-facing CTA. */
  usesClickThrough?: boolean
  // Whether this design writes durable rows to popup_responses (quizzes and
  // surveys do; teasers don't). Derived onto popups.collects_responses when a
  // popup is saved, rather than being an independent admin toggle — the
  // template decides what it records, so the two can't drift apart. The column
  // is what /api/popups/respond authorizes against.
  collectsResponses?: boolean
}

export const POPUP_DESIGNS: PopupDesign[] = [
  {
    kind: 'image',
    template: 'default',
    label: 'Image teaser',
    description: 'One clickable image in the bottom corner',
    usesClickThrough: true,
  },
  {
    kind: 'card',
    template: 'default',
    label: 'Card teaser',
    description: 'Title, text, CTA button and image in the bottom corner',
    usesClickThrough: true,
  },
  {
    kind: 'modal',
    template: 'general',
    label: 'General promo modal',
    description: 'Editable image, eyebrow, title, description, list, phone number and CTA',
    usesClickThrough: true,
  },
  {
    kind: 'modal',
    template: 'otc-quiz',
    label: 'OTC candidacy quiz',
    description: 'Multi-step self-check with a results screen and an in-popup Amy booking flow — copy fixed in code',
    collectsResponses: true,
  },
  {
    kind: 'modal',
    template: 'hearing-survey',
    label: 'Hearing aid audience survey',
    description:
      'Ten questions for general readers, plus three purchase questions for active shoppers and up to two tinnitus follow-ups, saved as they answer with one relevant follow-up action',
    collectsResponses: true,
  },
  {
    kind: 'modal',
    template: 'hearing-guide',
    label: 'Hearing Guide invite',
    description:
      'Navy invite modal mirroring the /hearing-guide page — an animated sample exchange plays inside a miniature chat panel. Design and copy fixed in code; the row supplies the CTA URL',
    usesClickThrough: true,
  },
  {
    kind: 'modal',
    template: 'suggest-app',
    label: 'Suggest an app (reader survey)',
    description:
      'Three-step reader suggestion form — app name, category, optional store links. Built for inline embedding via the popup_inline story blok; leave it disabled unless it should also trigger as a popup',
    collectsResponses: true,
  },
]

// Whether a saved popup should collect durable responses, from the registry
// above. Unknown template → false: a design that hasn't declared itself a
// response collector doesn't get to write rows.
export const designCollectsResponses = (kind: PopupKind, template: string): boolean =>
  POPUP_DESIGNS.find((d) => d.kind === kind && d.template === template)?.collectsResponses ?? false

export const designUsesClickThrough = (kind: string, template: string): boolean =>
  (kind === 'modal' && template === 'captioncall') ||
  (POPUP_DESIGNS.find((design) => design.kind === kind && design.template === template)?.usesClickThrough ?? false)

// The forum renders campaigns inside its own React/theme system. The portable
// teasers and data-driven general modal have native counterparts; code-defined
// modal flows stay main-site-only until their behavior is deliberately ported.
export const isForumNativePopupDesign = (kind: string, template: string): boolean =>
  ((kind === 'card' || kind === 'image') && template === 'default') || (kind === 'modal' && template === 'general')

// kind → template ids, derived from the registry (used for validation and
// template-select fallbacks).
export const POPUP_TEMPLATES: Record<PopupKind, string[]> = POPUP_DESIGNS.reduce(
  (acc, d) => {
    acc[d.kind].push(d.template)
    return acc
  },
  { image: [], card: [], modal: [] } as Record<PopupKind, string[]>
)

// Read/save compatibility while environments roll through the CaptionCall
// data migration. It is deliberately absent from POPUP_DESIGNS, so admins can
// no longer create a new hardcoded CaptionCall modal.
POPUP_TEMPLATES.modal.push('captioncall')

// Countries offered in the admin picker (ISO 3166-1 alpha-2 — matching accepts
// any code; this list is just the dropdown). Names render via
// Intl.DisplayNames, so nothing here needs translating or maintaining.
export const POPUP_COUNTRY_CODES = [
  'US',
  'CA',
  'GB',
  'IE',
  'AU',
  'NZ',
  'ZA',
  'DE',
  'AT',
  'CH',
  'FR',
  'BE',
  'NL',
  'DK',
  'SE',
  'NO',
  'FI',
  'ES',
  'IT',
  'PT',
  'MX',
  'BR',
  'IN',
  'CN',
  'JP',
  'SG',
] as const

// Event-name prefix: popup-<slug>--impression / --click / --dismiss. Impressions
// take the standard 10% sample to PostHog (see lib/analytics/sampling.ts);
// site_events keeps the exact stream, and reporting scales sampled rows ×10.
export const popupEventName = (slug: string) => `popup-${slug}`
